import React from 'react';
import { AlertBar } from '@packages/ui/shared';
import { CidCvnCheckResult } from '@packages/models/api/cid-cvn';
import { CidCvnCheckResultBadge } from '../components/cid-cvn-check-result-badge';
import { createUseStyles } from 'react-jss';
import { DetailViewBody } from '../../components/detail-view';
import { getDisplayValue } from '@web/utils';
import { ImagePreviewInfo } from './image-preview-info';
import { LoadingCircle, Typography } from '@packages/ui/shared';
import { TechlineStatusCode } from '@packages/models/api';
import { useAuthState } from '@packages/contexts/auth';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';

const useStyles = createUseStyles({
    contentContainer: {
        maxHeight: '70vh',
        overflow: 'auto',
    },
    table: {
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
        tableLayout: 'fixed',
        width: '100%',
    },
    tableHeader: {
        backgroundColor: colors.grayOne,
        borderBottom: `1px solid ${colors.grayThree}`,
        height: 56,
    },
    tableBody: {
        height: 56,
    },
    borderRight: {
        borderRight: `1px solid ${colors.grayThree}`,
    },
    li: {
        fontSize: 15,
    },
    loading: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        minHeight: '70vh',
    },
    span: {
        color: '#0D6EFD',
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'underline',
            color: '#0A58CA',
        },
    },
});

export interface CidCvnReportContentProps {
    caseNumber?: string;
    caseStatus?: TechlineStatusCode;
    createdBy: string;
    createdDate: string;
    cid: string;
    cvn: string;
    fileName: string;
    imageUrl: string;
    isLoading: boolean;
    result: CidCvnCheckResult;
    resultMessage: string;
    resultMessageReasons: string[];
    showDetails: boolean;
    onNavigateToTechlineCase?: () => void;
    onShowStartTechlineCaseModal?: () => void;
}

export function CidCvnReportContent({
    caseNumber,
    caseStatus,
    createdBy,
    createdDate,
    cid,
    cvn,
    fileName,
    imageUrl,
    isLoading,
    result,
    resultMessage,
    resultMessageReasons,
    showDetails,
    onNavigateToTechlineCase,
    onShowStartTechlineCaseModal,
}: CidCvnReportContentProps) {
    const { account } = useAuthState();
    const { techlineCapabilities } = account || {};
    const { t } = useTranslation();
    const classes = useStyles();

    const canCreateCidCvnCase =
        !!techlineCapabilities?.canCreateDLTechlineCase && !!techlineCapabilities?.canAddWorksheetTechlineCase;
    const isNoMatchFound = result === CidCvnCheckResult.NoMatchFound;
    const isDraftCaseStatus = caseStatus === TechlineStatusCode.DRAFT;

    const showCreateCaseAlertBar = !caseNumber && isNoMatchFound && canCreateCidCvnCase;
    const showViewCaseAlertBar = !!caseNumber && isNoMatchFound;

    const canViewCase = isDraftCaseStatus
        ? techlineCapabilities?.canViewTechlineCase && techlineCapabilities?.canViewDraftCase
        : techlineCapabilities?.canViewTechlineCase;

    return (
        <DetailViewBody className="p-0">
            <div className={classes.contentContainer}>
                {isLoading ? (
                    <div className={classes.loading} data-testid="loading-circle">
                        <LoadingCircle size={64} />
                    </div>
                ) : (
                    <div>
                        {showDetails && (
                            <>
                                {showCreateCaseAlertBar ? (
                                    <AlertBar show warning iconHide={true} onClose={() => {}}>
                                        <Typography>
                                            {t('cidCvnCheck:report.alertBarMessages.noMatchFound.needsTechReview')}
                                            {'  '}
                                            <span
                                                className={classes.span}
                                                role="button"
                                                onClick={onShowStartTechlineCaseModal}
                                            >
                                                {t('cidCvnCheck:report.alertBarMessages.createTechlineCase')}
                                            </span>
                                        </Typography>
                                    </AlertBar>
                                ) : showViewCaseAlertBar ? (
                                    <AlertBar show info iconHide={true} onClose={() => {}}>
                                        <Typography>
                                            {t(
                                                'cidCvnCheck:report.alertBarMessages.noMatchFound.caseOpenedForInvestigation'
                                            )}
                                            {'  '}
                                            {canViewCase && (
                                                <span
                                                    className={classes.span}
                                                    role="button"
                                                    onClick={onNavigateToTechlineCase}
                                                >
                                                    {t('cidCvnCheck:report.alertBarMessages.viewTechlineCase')}
                                                </span>
                                            )}
                                        </Typography>
                                    </AlertBar>
                                ) : null}
                            </>
                        )}
                        <div className="d-flex flex-column p-6">
                            <div className="mb-6">
                                <Typography variant="h3">{t('cidCvnCheck:report.title')}</Typography>
                            </div>
                            <div className="d-flex flex-row mb-6">
                                <Typography variant="label">{t('cidCvnCheck:report.result')}:</Typography>
                                <CidCvnCheckResultBadge style={{ marginLeft: 24 }} result={result} />
                            </div>
                            <div className="mb-6">
                                <Typography>{resultMessage}</Typography>
                                {resultMessageReasons.length > 0 && (
                                    <ol className={classes.li}>
                                        {resultMessageReasons.map((reason, index) => (
                                            <li key={index}>
                                                <Typography>{reason}</Typography>
                                            </li>
                                        ))}
                                    </ol>
                                )}
                            </div>

                            {showDetails && (
                                <>
                                    <div className="mb-6">
                                        <table className={classes.table}>
                                            <thead className={classes.tableHeader}>
                                                <tr>
                                                    <th className={`${classes.borderRight} pl-4`}>
                                                        <Typography variant="label">
                                                            {t('cidCvnCheck:report.cid')}
                                                        </Typography>
                                                    </th>
                                                    <th className="pl-4">
                                                        <Typography variant="label">
                                                            {t('cidCvnCheck:report.cvn')}
                                                        </Typography>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className={classes.tableBody}>
                                                <tr>
                                                    <td className={`${classes.borderRight} pl-4`}>
                                                        <Typography>{getDisplayValue(cid)}</Typography>
                                                    </td>
                                                    <td className="pl-4">
                                                        <Typography>{getDisplayValue(cvn)}</Typography>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="mb-3">
                                        <Typography>{t('cidCvnCheck:report.screenshotUsed')}:</Typography>
                                    </div>
                                    <ImagePreviewInfo
                                        fileName={fileName}
                                        previewUrl={imageUrl}
                                        uploadedAt={createdDate}
                                        uploadedBy={createdBy}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </DetailViewBody>
    );
}
